import { StationSource } from './../core/models/station-source';
import { IStationCardField } from './../shared/station-card/station-card.model';
import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, Pipe } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { forkJoin, Observable, BehaviorSubject } from 'rxjs';
import { delay } from 'rxjs/operators';
import { Filter, ProfileModelService } from '../profile/profile-model.service';
import { ProfileService } from '../profile/profile.service';
import { BaseSearchDialogComponent } from '../search-dialog/base-search-dialog.component';
import { DialogData } from '../search-dialog/search-dialog.component';
import { SearchFiltersService } from '../search-filters/search-filters.service';
import { MarketFormatPipe } from '../shared/market-format.pipe';
import { MultiDropboxComponent, MultiDropboxData } from '../shared/multi-dropbox';
import { TruncateTextPipe } from '../shared/truncate-text.pipe';
import { PlaylistService, Station, StationService } from '../core';
import { SearchFilterAggregate } from '../search-filters/search-filters.service';
import { PlayerSidenavComponent, PlayerSidenavReferencesService } from '../shared';

import { fromEvent, Subscription } from 'rxjs';
import { MergedStation } from '../shared/model/merged-station.interface';
import { StationUtilsService } from '../shared/utils/station-utils.service';

export interface StationFilter {
  markets: MultiDropboxData[];
  formats: MultiDropboxData[];
  stations: MultiDropboxData[];
}

declare var dataLayer: any;

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss', './home-dark.component.scss'],
  providers: [StationService, SearchFiltersService, MarketFormatPipe, TruncateTextPipe]
})
export class HomeComponent extends BaseSearchDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('stationMulti', { static: true }) stationMulti: MultiDropboxComponent;
  @ViewChild('marketMulti', { static: true }) marketMulti: MultiDropboxComponent;
  @ViewChild('formatMulti', { static: true }) formatMulti: MultiDropboxComponent;
  @ViewChild('savedFiltersMulti', { static: true }) savedFiltersMulti: MultiDropboxComponent;
  stations: MultiDropboxData[] = [];
  markets: MultiDropboxData[] = [];
  formats: MultiDropboxData[] = [];
  filterToApply: MultiDropboxData;
  stationsData: MergedStation[] = [];
  favoriteStationsData: Station[] = [];
  pageNumber = 0;
  pageSize = 40;
  cardsGridClass = 'cardGrid col-sm-4 col-md-3 col-lg-2 col-xl-2';
  uniqueMarkets: string[] = [];
  isPlayerOpen = false;
  selectable = true;
  stationLoadDelay = 100; // Delay in milliseconds

  private uniqueDefaultMarkets: string[] = [];
  private uniqueFilteredMarkets: string[] = [];

  origMarketsList: any;
  origStationsList: any;
  origFormatsList: any;

  resizeObservable$: Observable<Event>;
  resizeSubscription$: Subscription;

  isSafari = false;

  constructor(
    private router: Router,
    private stationService: StationService,
    private playlistService: PlaylistService,
    private playerSidenavReferenceService: PlayerSidenavReferencesService,
    private searchFiltersService: SearchFiltersService,
    public dialog: MatDialog,
    profileService: ProfileService,
    private titleService: Title,
    profileModelService: ProfileModelService,
    private stationUtilsService: StationUtilsService) {
    super(dialog, profileService, profileModelService);
    this.isSafari = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1;
    this.titleService.setTitle('SIA (Search, Index, Archive) - Home');
  }

  afterSavedDialog(filterName: string) {
    this.refreshSavedFilters();

    this.filterToApply = {
      id: filterName,
      name: filterName
    } as MultiDropboxData;

    this.searchChange();
  }

  refreshSavedFilters(): void {
    const savedFilterStations = this.profileModelService.getStationFilters() || [];
    this.savedFiltersMulti.setList(savedFilterStations.map((filter: Filter) => {
      return {
        id: filter.filterName,
        name: filter.filterName
      } as MultiDropboxData;
    }));
  }

  applySavedFilter(item: MultiDropboxData): void {
    const filterName = item.id;
    const filter: Filter = this.profileModelService.getStationFilters()
      .find((stationFilter: Filter) => stationFilter.filterName === filterName);

    if (filter) {
      this.applyFilter(filter.markets, filter.formats, filter.stations);
    }
  }

  applyFilter(marketIds: string[], formatIds: string[], stationIds: string[]): void {
    this.buildFilterData(marketIds, formatIds, stationIds)
      .subscribe((defaultFilter: StationFilter) => {
        if (defaultFilter) {
          this.formats = defaultFilter.formats;
          this.markets = defaultFilter.markets;
          this.stations = defaultFilter.stations;

          this.loadStations();
          this.loadFormats();
          this.searchChange();
        }
      });
  }

  buildFilterData(marketIds: string[], formatIds: string[], stationIds: string[]): Observable<StationFilter> {
    const _defaultFilter: BehaviorSubject<StationFilter> = new BehaviorSubject(null) as BehaviorSubject<StationFilter>;
    const defaultFilter = _defaultFilter.asObservable();

    let markets: MultiDropboxData[];
    let formats: MultiDropboxData[];
    let stations: MultiDropboxData[];

    if ((marketIds && marketIds.length > 0)
      || (formatIds && formatIds.length > 0)
      || (stationIds && stationIds.length > 0)) {
      /* The URL will receive only ids as params, but the logic to update the multidropbox
      expects complete objects, so we need to query the stations only to rebuild those objects.
      */
      forkJoin([
        this.searchFiltersService.loadAllMarkets(),
        this.searchFiltersService.loadAllFormats(),
        this.searchFiltersService.loadAllStations()
      ]).subscribe(
        (values) => {
          const marketsResult: SearchFilterAggregate[] = values[0];
          const formatsResult: SearchFilterAggregate[] = values[1];
          const stationsResult: Station[] = values[2];

          markets = marketsResult.filter((marketFilter: SearchFilterAggregate) => marketIds.indexOf(marketFilter.name) >= 0)
            .map((marketFilter: SearchFilterAggregate) => {
              return {
                id: marketFilter.aggregate,
                name: marketFilter.name,
              } as MultiDropboxData;
            });

          formats = formatsResult.filter((formatFilter: SearchFilterAggregate) => formatIds.indexOf(formatFilter.name) >= 0)
            .map((formatFilter: SearchFilterAggregate) => {
              return {
                id: formatFilter.name,
                name: formatFilter.name,
                model: formatFilter
              } as MultiDropboxData;
            });
          const validStations = stationsResult.filter((station: Station) => stationIds.indexOf(station.id) >= 0);
          stations = this.stationUtilsService.toMultiDropboxDataGroupedByName(validStations);
        },
        (error) => {
          // TODO: Implement the error popup or something to display the issue on load the filters
        },
        () => {
          _defaultFilter.next({
            stations: stations || [],
            formats: formats || [],
            markets: markets || []
          } as StationFilter);
        }
      );
    } else {
      _defaultFilter.next({
        stations: [],
        formats: [],
        markets: []
      } as StationFilter);
    }

    return defaultFilter;
  }

  editStationFilter(item: MultiDropboxData): void {
    const filterName = item.id;
    const filter: Filter = this.profileModelService.getStationFilters()
      .find((stationFilter: Filter) => stationFilter.filterName === filterName);

    if (filter) {
      this.buildFilterData(filter.markets, filter.formats, filter.stations)
        .subscribe((stationFilter: StationFilter) => {
          if (stationFilter) {
            this.openEditDialogFilter({
              filterName: filter.filterName,
              oldFilterName: filter.filterName,
              stations: [].concat(stationFilter.stations),
              markets: [].concat(stationFilter.markets),
              formats: [].concat(stationFilter.formats),
              origMarketsList: [].concat(this.origMarketsList),
              origStationsList: [].concat(this.origStationsList),
              origFormatsList: [].concat(this.origFormatsList),
              isHomePage: true
            });
          }
        });
    }
  }

  dialogUpdate(dialogData: DialogData) {
    if (this.profileModelService.getDefaultStationFilter().filterName === dialogData.oldFilterName) {
      this.profileModelService.setDefaultStationFilter(dialogData.filterName);
    }

    this.profileModelService.addStationFilter({
      formats: dialogData.formats.map((formatMulti: MultiDropboxData) => formatMulti.name),
      markets: dialogData.markets.map((marketMulti: MultiDropboxData) => marketMulti.name),
      // Because station is grouped by their call letter, they have an array of string ids instead of a single string id
      stations: dialogData.stations.map((stationMulti: MultiDropboxData) => stationMulti.id).flat(),
      filterName: dialogData.filterName
    });

    this.markets = dialogData.markets;
    this.formats = dialogData.formats;
    this.stations = dialogData.stations;
  }

  ngAfterViewInit() {
    this.resizeToWidth(window.innerWidth - 400);
  }

  ngOnInit() {

    this.loadFavorites();
    this.getPlayerSidenav().title = 'Live Station Audio';
    this.loadControls();
    this.refreshSavedFilters();

    if (this.getPlayerSidenav() && this.getPlayerSidenav().expandOrCollapseEventEmitter) {
      this.getPlayerSidenav().expandOrCollapseEventEmitter.subscribe(isOpen => {
        this.isPlayerOpen = isOpen;
        this.resizeToWidth(window.innerWidth - 400);
      });
    }

    // Fill all filters with profile configuration
    const stationFilter: Filter = this.profileModelService.getDefaultStationFilter();

    const marketIds: string[] = stationFilter.markets;
    const formatIds: string[] = stationFilter.formats;
    const stationIds: string[] = stationFilter.stations;

    this.applyFilter(marketIds, formatIds, stationIds);

    // window resize observer
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.resizeSubscription$ = this.resizeObservable$.subscribe((evt: UIEvent) => {
      const winWidth = (evt.target as Window).innerWidth;
      const playerWidth = 400;
      const cardsGridWidth = winWidth - playerWidth;
      this.resizeToWidth(cardsGridWidth);
    });
  }

  resizeToWidth(cardsGridWidth: number): void {
    // cardsGrid > 900
    if (cardsGridWidth > 900 && !this.isPlayerOpen) {
      this.cardsGridClass = 'cardGrid col-sm-4 col-md-3 col-lg-2 col-xl-2';
      return;
    }

    if (cardsGridWidth > 900 && this.isPlayerOpen) {
      this.cardsGridClass = 'cardGrid col-sm-6 col-md-4 col-lg-3 col-xl-3';
      return;
    }

    // cardsGrid > 700
    if (cardsGridWidth > 700 && !this.isPlayerOpen) {
      this.cardsGridClass = 'cardGrid col-sm-6 col-md-4 col-lg-3 col-xl-3';
      return;
    }

    if (cardsGridWidth > 700 && this.isPlayerOpen) {
      this.cardsGridClass = 'cardGrid col-sm-7 col-md-5 col-lg-4 col-xl-3';
      return;
    }

    // cardsGrid < 700 (all other scenarios)
    if (this.isPlayerOpen) {
      this.cardsGridClass = 'cardGrid col-sm-12 col-md-12 col-lg-12 col-xl-12';
    } else {
      this.cardsGridClass = 'cardGrid col-sm-7 col-md-4 col-lg-3 col-xl-2';
    }
    return;
  }

  loadFavorites() {
    const favoriteIds: string[] = this.profileModelService.getFavoriteStations();
    this.favoriteStationsData = [];
    
    if (favoriteIds.length > 0) {
      this.stationService.findStationsByCriteria(favoriteIds, [], [], null, favoriteIds.length).subscribe((stations: Station[]) => {
        stations.filter(station =>{ return favoriteIds.includes(station.id)}).forEach((station: Station) => {
          station.favorite = true;
          this.favoriteStationsData.push(station);
        });

      });

    }
  }

  loadControls() {
    this.loadMarkets();
    this.loadFormats();
    this.loadStations();
  }

  loadMarkets() {
    this.searchFiltersService.loadAllMarkets().subscribe((markets: SearchFilterAggregate[]) => {
      this.origMarketsList = markets;
      this.marketMulti.setList(markets.map((marketFilter: SearchFilterAggregate) => {
        return {
          id: marketFilter.aggregate,
          name: marketFilter.name
        };
      }));
    });
  }

  loadFormats() {
    this.searchFiltersService.loadAllFormats().subscribe((formats: SearchFilterAggregate[]) => {
      this.origFormatsList = formats;
      const filterFormats = formats.reduce((combiner, transformer) => {
        if (
          this.markets == null
          || this.markets.length <= 0
          || transformer.aggregate.filter(market => this.markets.reduce(
            (aggregate, marketFilter) => aggregate.concat(marketFilter.id), []
          ).includes(market)).length > 0
        ) {
          combiner.push(transformer);
        }

        return combiner;
      }, []);

      this.formatMulti.setList(filterFormats.map((format) => {
        return {
          id: String(format.name),
          name: String(format.name),
          model: format
        };
      }));
    });
  }

  loadStations() {
    this.searchFiltersService.loadAllStations().pipe(delay(this.stationLoadDelay)).subscribe((stations: Station[]) => {
      this.origStationsList = stations;

      let filterStations = stations;

      if (this.markets != null && this.markets.length > 0) {
        filterStations = filterStations.filter((station) => this.markets.reduce(
          (aggregate, marketFilter) => aggregate.concat(marketFilter.id), []
        ).includes(station.market));
      }

      if (this.formats != null && this.formats.length > 0) {
        filterStations = filterStations.filter((station) => this.formats.map(format => format.id).includes(station.format));
      }

      this.stationMulti.setList(filterStations.map((station) => {
        const name = station.name ? station.name : '';
        const frequency = station.frequency ? station.frequency : '';

        return {
          id: station.id,
          name: `${name} ${frequency}`,
          model: station
          
        };
      }));
    });
  }

  searchChange() {
    this.reset();
    this.search();
  }

  reset() {
    this.pageNumber = 0;
    this.uniqueDefaultMarkets = [];
    this.uniqueFilteredMarkets = [];
    this.uniqueMarkets = [];
    this.stationsData = [];
  }

  search() {
    const stationList: string[] = this.stations != null ? this.stations.map(station => station.id).flat() : [];
    const formatList: string[] = this.formats != null ? this.formats.map(format => format.id) : [];
    const marketList: string[] = this.markets != null ? this.markets.reduce(
      (aggregate, marketFilter) => aggregate.concat(marketFilter.id), []
    ) : [];

    if (stationList.length > 0
      || formatList.length > 0
      || marketList.length > 0
      || this.profileModelService.getDefaultMarkets().length === 0) {
      this.loadStationList(stationList, formatList, marketList);
    } else {

      this.stationService.findStationsByCriteria([], [], this.profileModelService.getDefaultMarkets(), this.pageNumber, this.pageSize)
        .subscribe((stations: Station[]) => {
          this.loadStationList(stationList, formatList, marketList, stations, this.profileModelService.getDefaultMarkets());
        });
    }
  }

  private loadStationList(
    stationList: string[],
    formatList: string[],
    marketList: string[],
    defaultStations: Station[] = [],
    ignoreMarkets: string[] = []
  ) {
    this.stationService.findStationsByCriteria(
      stationList,
      formatList,
      marketList,
      this.pageNumber,
      this.pageSize
    ).subscribe((stations: Station[]) => {

      const filteredStations = stations.filter((station: Station) => {
        return !new Set(ignoreMarkets).has(station.market);
      });

      const resultStations = new Set([...defaultStations, ...filteredStations]);

      const distinctDefaultMarkets = new Set(defaultStations.map(item => item.market).sort());
      const distinctFilteredMarkets = new Set(filteredStations.map(item => item.market).sort());

      distinctDefaultMarkets.forEach((market: string) => {
        if (this.uniqueDefaultMarkets.indexOf(market) === -1) {
          this.uniqueDefaultMarkets.push(market);
        }
      });

      distinctFilteredMarkets.forEach((market: string) => {
        if (this.uniqueFilteredMarkets.indexOf(market) === -1) {
          this.uniqueFilteredMarkets.push(market);
        }
      });

      this.uniqueMarkets = [...this.uniqueDefaultMarkets, ...this.uniqueFilteredMarkets];


      const overlapStations: Station[] = [];
      for (const station of resultStations) {
        const overlapStation = this.stationsData.filter(data => data.name === station.name);
        if (overlapStation.length === 1) {
          this.stationsData.forEach((obj, index) => {
            if (overlapStation[0].id === obj.id) {
              this.stationsData.splice(index, 1);
            }
          });
          overlapStations.push(overlapStation[0].model[0]);
        }
      }

      overlapStations.forEach(station => resultStations.add(station));

      this.stationsData = this.stationsData.concat(this.stationService.groupStationByName(Array.from(resultStations)));
    });
  }

  removeMarket(market: MultiDropboxData) {
    this.markets = this.markets.filter(item => item !== market);
    this.formats = this.formats.filter(item => item.model.aggregate.filter(
      formatMarkets => new Set(this.markets.reduce(
        (aggregate, marketFilter) => aggregate.concat(marketFilter.id), []
      )).has(formatMarkets)).length > 0);
    this.stations = this.stations.filter(item => !new Set(market.id).has(item.model.market));

    this.loadFormats();
    this.loadStations();
    this.searchChange();
  }

  removeFormat(format: MultiDropboxData) {
    this.formats = this.formats.filter(item => item !== format);
    this.stations = this.stations.filter(item => item.model.format !== format.id);
    this.loadStations();
    this.searchChange();
  }

  removeStation(station: MultiDropboxData) {
    this.stations = this.stations.filter(item => item !== station);
    this.searchChange();
  }


  clearFilters(event: any) {
    this.stations = [];
    this.formats = [];
    this.markets = [];
    this.filterToApply = null;
    this.searchChange();
  }

  getMarketStations(market: string): MergedStation[] {
    return this.stationsData.filter(x => x.model[0].market === market);
  }

  favoriteChangeEvent(favoriteModel) {
    if (favoriteModel.favorite) {
      this.favoriteStationsData.push(favoriteModel);
      try {
        dataLayer.push({
          event: 'click-favorite',
          operation: 'favorite-added'
        });
      } catch (e) { }
    } else {
      this.favoriteStationsData = this.favoriteStationsData.filter((favorite) => favorite.id !== favoriteModel.id);
    }

    this.profileModelService.setFavoriteStations(this.favoriteStationsData.map((favorite: Station) => favorite.id));

    this.profileService.updateConfiguration(this.profileModelService.configuration).subscribe(
      (profile) => {
        this.profileModelService.profile = profile;
      }
    );

    this.searchChange();
  }

  play(station: Station) {
    const playerSidenav: PlayerSidenavComponent = this.getPlayerSidenav();
    if (!this.isPlaying(station.id)) {
      if (!playerSidenav.isOpened(station.id)) {
        playerSidenav.close('selected');
        const playlistDetails = this.playlistService.buildLiveURL(+station.id);
        const playlistURL: string = playlistDetails.url;
        playerSidenav.open(station, playlistURL, true);

      } else {
        playerSidenav.play();
      }
    } else {
      playerSidenav.pause();
    }
  }

  isPlaying(stationId: string): boolean {
    /* TODO: working with events rather than
    pooling the player all the time? */
    return this.getPlayerSidenav().isPlaying(stationId);
  }

  onScroll() {
    this.pageNumber++;
    this.search();
  }



  ngOnDestroy() {
    this.resizeSubscription$.unsubscribe();
  }

  private getPlayerSidenav(): PlayerSidenavComponent {
    return this.playerSidenavReferenceService.get('right');
  }

  private getSoundPlusStation(list: Station[]): Station {
    if (list.length === 1) {
      return list[0];
    }

    for (const station of list) {
      if (this.stationUtilsService.filterStationSourceStream([station.stationSource]).length>0) {
        return station;
      }
    }
  }

  
}
